import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Add, Balance, CheckCircle, Label, PushPin, Search } from '@mui/icons-material'
import { Box, Button, Card, InputAdornment, MenuItem, Skeleton, Stack, TableContainer, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { GameIcon } from '../../../features/game'
import { Game } from '../../../features/game/types/Game'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import {
  useDeleteInternalGameMutation,
  useGetInternalPinnedGamesQuery,
  useLazySearchInternalGameListQuery,
  useSaveInternalPinnedGamesMutation,
  useSearchInternalGameListQuery,
} from '../../api/games'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { AnalysisListDialog } from '../../components/AnalysisListDialog/AnalysisListDialog'
import { EditGameDialog } from '../../components/EditGameDialog/EditGameDialog'
import { ErrorStatusCircle } from '../../components/ErrorStatusCircle/ErrorStatusCircle'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { VersionTagsDialog } from '../../components/VersionTagsDialog/VersionTagsDialog'
import { getCreatedAnalysisPath } from '../../features/analysis/helpers/analysisPaths'
import { GameIdSearchDialog } from '../../features/games/components/GameIdSearchDialog/GameIdSearchDialog'
import { GameMarketsCell } from '../../features/games/components/GameMarketsCell/GameMarketsCell'
import { GameNameCell } from '../../features/games/components/GameNameCell/GameNameCell'
import {
  filterListGames,
  formatLastAnalysisDate,
  gameSearchDebounceMs,
  getCellMarketIso,
  getLastAnalysisAt,
  getRoundedPowerscore,
  isLastAnalysisOlderThan,
  minGameSearchTermLength,
  outdatedAnalysisMonths,
  togglePinnedGameId,
} from '../../features/games/helpers/gameListHelpers'
import { AnalyzedFilter, allMarketsIso, InternalGameDocument } from '../../features/games/types/InternalGame'
import { ReviewStatusIndicator } from '../../features/review/components/ReviewStatusIndicator/ReviewStatusIndicator'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

const analyzedFilters = [
  { value: AnalyzedFilter.Analyzed, labelKey: 'internal-analysts:games_filter_analyzed' },
  { value: AnalyzedFilter.NotAnalyzed, labelKey: 'internal-analysts:games_filter_not_analyzed' },
  { value: AnalyzedFilter.All, labelKey: 'internal-analysts:games_filter_all' },
]
const parseAnalyzedFilter = (value: string | null) => analyzedFilters.find((filter) => filter.value === value)?.value ?? AnalyzedFilter.Analyzed

type CustomProps = {
  cellMarketIso: string
  pinnedIds: string[]
  canPin: boolean
  onPin: (game: Game) => void
  onViewAnalyses: (game: Game) => void
  onEditVersionTags: (game: Game) => void
  onEdit: (game: Game) => void
  onDelete: (game: Game) => void
}

type GameDialogState = { open: boolean; gameId?: string }
type VersionTagsDialogState = { open: boolean; appId?: number; gameName: string }

const toListGame = (stored: InternalGameDocument) => new Game(stored as unknown as Game)
const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index])

export const GamesPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-games'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const marketIso = searchParams.get('marketIso') || allMarketsIso
  const analyzed = parseAnalyzedFilter(searchParams.get('analyzed'))
  const urlTerm = searchParams.get('term') ?? ''
  const searchTerm = urlTerm.trim()
  const [term, setTerm] = useState(urlTerm)
  // The term this page last wrote to the URL; any other URL term came from navigation (e.g. back) and replaces the input.
  const writtenTermRef = useRef(urlTerm)
  const [addedGames, setAddedGames] = useState<Game[]>([])
  const [gameDialog, setGameDialog] = useState<GameDialogState>({ open: false })
  const [isIdSearchOpen, setIsIdSearchOpen] = useState(false)
  const [analysisDialog, setAnalysisDialog] = useState<GameDialogState>({ open: false })
  // Legacy opens the analysis list of a new game once its dialog closes; the dialog calls `onClose` right after `onSaved`.
  const addedGameIdRef = useRef<string>()
  const [versionTagsDialog, setVersionTagsDialog] = useState<VersionTagsDialogState>({ open: false, gameName: '' })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<Game>>()

  const setParam = useCallback(
    (key: string, value: string, defaultValue: string) =>
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params)
          if (value && value !== defaultValue) next.set(key, value)
          else next.delete(key)
          return next
        },
        { replace: true }
      ),
    [setSearchParams]
  )

  useEffect(() => {
    if (urlTerm === writtenTermRef.current) return
    writtenTermRef.current = urlTerm
    setTerm(urlTerm)
  }, [urlTerm])

  useEffect(() => {
    if (term === urlTerm) return
    const timeout = setTimeout(() => {
      writtenTermRef.current = term
      setParam('term', term, '')
    }, gameSearchDebounceMs)
    return () => clearTimeout(timeout)
  }, [term, urlTerm, setParam])

  // Added games stay on top until the next search, as legacy appends them whatever the filter.
  useEffect(() => setAddedGames([]), [searchTerm, marketIso])

  const canSearch = searchTerm.length >= minGameSearchTermLength
  const searchQuery = useSearchInternalGameListQuery({ term: searchTerm, marketIso }, { skip: !canSearch })
  const [searchGames] = useLazySearchInternalGameListQuery()
  const marketsQuery = useGetInternalMarketsQuery()
  const pinnedQuery = useGetInternalPinnedGamesQuery()
  const [savePinnedGames, { isLoading: isSavingPinned }] = useSaveInternalPinnedGamesMutation()
  const [deleteGame] = useDeleteInternalGameMutation()

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const rows = useMemo(() => {
    const searched = filterListGames(searchQuery.currentData ?? [], marketIso, analyzed)
    const addedIds = new Set(addedGames.map(({ id }) => id))
    return [...addedGames, ...searched.filter(({ id }) => !addedIds.has(id))]
  }, [addedGames, analyzed, marketIso, searchQuery.currentData])
  const isSearching = (canSearch && searchQuery.isFetching) || (term.trim() !== searchTerm && term.trim().length >= minGameSearchTermLength)

  const pinnedIds = useMemo(() => pinnedQuery.data ?? [], [pinnedQuery.data])
  // Legacy sends the whole list, so an unread list would wipe the other pins.
  const canPin = !!pinnedQuery.data && !pinnedQuery.isFetching && !isSavingPinned

  const handlePin = useCallback(
    async (game: Game) => {
      const pinning = !pinnedIds.includes(game.id)
      const next = togglePinnedGameId(pinnedIds, game.id)
      const saved = await run(savePinnedGames(next).unwrap(), { error: t('internal-analysts:games_pin_failed') })
      if (!saved.ok) return
      if (!sameIds(saved.result, next)) {
        notifyError(t('internal-analysts:games_pin_not_stored'))
        return
      }
      notifySuccess(t(pinning ? 'internal-analysts:games_pinned' : 'internal-analysts:games_unpinned', { name: game.name }))
    },
    [notifyError, notifySuccess, pinnedIds, run, savePinnedGames, t]
  )

  const askDelete = useCallback(
    (game: Game) =>
      setConfirmDialogData({
        title: t('internal-analysts:games_delete'),
        confirmText: t('internal-analysts:games_delete_confirm', { name: game.name || '---' }),
        actionText: t('internal-common:delete'),
        destructiveAction: true,
        data: game,
      }),
    [t]
  )

  // Searched by the game's own name in all markets, not the search on screen, which may not contain the game. Not a read
  // of the id: the gateway's 404 has no CORS headers, and whether a deleted game still reads back is unconfirmed.
  const isGameDeleted = useCallback(
    async (game: Game) => {
      const found = await searchGames({ term: game.name ?? '', marketIso: allMarketsIso }).unwrap()
      return !found.some(({ id }) => id === game.id)
    },
    [searchGames]
  )

  const { refetch } = searchQuery
  const handleConfirmDelete = useCallback(
    async (confirmed?: ConfirmDialogData<Game>) => {
      setConfirmDialogData(undefined)
      const game = confirmed?.data
      if (!game) return
      const name = game.name || '---'
      try {
        await deleteGame(game.id).unwrap()
        const isDeleted = await isGameDeleted(game)
        if (canSearch) refetch()
        if (!isDeleted) {
          notifyError(t('internal-analysts:games_not_deleted', { name }))
          return
        }
        setAddedGames((current) => current.filter(({ id }) => id !== game.id))
        notifySuccess(t('internal-analysts:games_deleted', { name }))
      } catch (error) {
        const reason = getValidationErrorMessage(error)
        notifyError(reason ? t('internal-analysts:games_delete_failed_reason', { name, reason }) : t('internal-analysts:games_delete_failed', { name }))
      }
    },
    [canSearch, deleteGame, isGameDeleted, notifyError, notifySuccess, refetch, t]
  )

  const handleSaved = (stored: InternalGameDocument) => {
    const game = toListGame(stored)
    setAddedGames((current) =>
      !gameDialog.gameId || current.some(({ id }) => id === game.id) ? [game, ...current.filter(({ id }) => id !== game.id)] : current
    )
    if (!gameDialog.gameId && game.id) addedGameIdRef.current = game.id
    if (canSearch) refetch()
  }

  const handleGameDialogClose = () => {
    setGameDialog({ open: false })
    const addedGameId = addedGameIdRef.current
    if (!addedGameId) return
    addedGameIdRef.current = undefined
    setAnalysisDialog({ open: true, gameId: addedGameId })
  }

  const customProps = useMemo<CustomProps>(
    () => ({
      cellMarketIso: getCellMarketIso(marketIso),
      pinnedIds,
      canPin,
      onPin: handlePin,
      onViewAnalyses: (game) => setAnalysisDialog({ open: true, gameId: game.id }),
      onEditVersionTags: (game) => setVersionTagsDialog({ open: true, appId: game.appId, gameName: game.name ?? '' }),
      onEdit: (game) => setGameDialog({ open: true, gameId: game.id }),
      onDelete: askDelete,
    }),
    [askDelete, canPin, handlePin, marketIso, pinnedIds]
  )

  const tableColumns: GRTableColumn<Game, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-common:icon'),
        accessor: ({ row }) => <GameIcon src={row.getIcon() as string} gameName={row.name ?? ''} size="small" />,
        headerCellProps: { sx: { width: 62, minWidth: 62 } },
      },
      {
        labelAccessor: t('internal-common:name'),
        accessor: ({ row }) => <GameNameCell game={row} />,
        sortable: true,
        sortAccessor: ({ row }) => (row.name ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-common:publisher'),
        accessor: ({ row }) => row.artist || '---',
        sortable: true,
        sortAccessor: ({ row }) => (row.artist ?? '').toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 120 } },
      },
      {
        labelAccessor: t('internal-common:powerscore'),
        accessor: ({ row, customTableProps }) => getRoundedPowerscore(row, customTableProps?.cellMarketIso ?? 'us') ?? '---',
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => getRoundedPowerscore(row, customTableProps?.cellMarketIso ?? 'us') ?? 0,
      },
      {
        labelAccessor: t('internal-common:last_analysis'),
        accessor: ({ row, customTableProps }) => {
          const timestamp = getLastAnalysisAt(row, customTableProps?.cellMarketIso ?? 'us')
          if (!timestamp) return '---'
          return (
            <Box component="span" whiteSpace="nowrap">
              {formatLastAnalysisDate(timestamp)}
              {isLastAnalysisOlderThan(timestamp, outdatedAnalysisMonths) && (
                <ErrorStatusCircle errorText={t('internal-common:last_analysis_over_6_months_ago')} />
              )}
            </Box>
          )
        },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => getLastAnalysisAt(row, customTableProps?.cellMarketIso ?? 'us') ?? 0,
        headerCellProps: { sx: { minWidth: 124 } },
      },
      {
        labelAccessor: t('internal-common:analyst_overview'),
        accessor: ({ row }) => (row.appId === null || row.appId === undefined ? '---' : <ReviewStatusIndicator game={row} />),
      },
      {
        labelAccessor: t('internal-common:stage'),
        accessor: ({ row }) => row.stage || '---',
        sortable: true,
        sortAccessor: ({ row }) => (row.stage ?? '').toLowerCase(),
      },
      {
        labelAccessor: t('internal-common:markets'),
        accessor: ({ row }) => <GameMarketsCell game={row} />,
        sortable: true,
        sortAccessor: ({ row }) => row.markets?.length ?? 0,
      },
      rowActionsColumn<Game, CustomProps>({
        width: 224,
        actions: ({ row, customTableProps }) => {
          const isPinned = !!customTableProps?.pinnedIds.includes(row.id)
          return [
            {
              icon: isPinned ? <CheckCircle /> : <PushPin />,
              label: t(isPinned ? 'internal-analysts:games_unpin' : 'internal-analysts:games_pin'),
              color: isPinned ? 'success' : 'primary',
              disabled: !customTableProps?.canPin,
              onClick: () => customTableProps?.onPin(row),
            },
            { icon: <Balance />, label: t('internal-analysts:games_view_analyses'), onClick: () => customTableProps?.onViewAnalyses(row) },
            {
              icon: <Label />,
              label: t(row.appId ? 'internal-analysts:games_edit_version_tags' : 'internal-analysts:games_edit_version_tags_needs_app_id'),
              ariaLabel: t('internal-analysts:games_edit_version_tags'),
              disabled: !row.appId,
              onClick: () => customTableProps?.onEditVersionTags(row),
            },
            { type: 'edit', label: t('internal-analysts:games_edit'), onClick: () => customTableProps?.onEdit(row) },
            { type: 'delete', label: t('internal-analysts:games_delete'), onClick: () => customTableProps?.onDelete(row) },
          ]
        },
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const renderResults = () => {
    if (!canSearch && !addedGames.length) {
      return <Typography color="text.secondary">{t('internal-analysts:games_search_min_length')}</Typography>
    }
    if (isSearching && !addedGames.length) return <Skeleton variant="rectangular" height={240} />
    if (searchQuery.isError && !addedGames.length) return <Typography color="error.main">{t('internal-analysts:games_search_failed')}</Typography>
    return (
      <TableContainer component={Card}>
        <GRTable
          rows={rows}
          columns={columns}
          onColumnsUpdated={setColumns}
          rowIdKey="id"
          customProps={customProps}
          noRowsLabel={t('internal-analysts:games_none')}
          isLoading={isSearching}
          striped
          hoverable
        />
      </TableContainer>
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<Search />} onClick={() => setIsIdSearchOpen(true)}>
            {t('internal-analysts:games_search_by_id')}
          </Button>
        </Stack>
        <TextField
          select
          size="small"
          label={t('internal-common:market')}
          value={marketIso}
          onChange={(event) => setParam('marketIso', event.target.value, allMarketsIso)}
          error={marketsQuery.isError}
          helperText={marketsQuery.isError ? t('internal-analysts:games_markets_load_failed') : undefined}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value={allMarketsIso}>{t('internal-analysts:games_market_all')}</MenuItem>
          {markets.map((market) => (
            <MenuItem key={market.id} value={market.iso}>
              {market.name}
            </MenuItem>
          ))}
          {marketIso !== allMarketsIso && !markets.some(({ iso }) => iso === marketIso) && <MenuItem value={marketIso}>{marketIso}</MenuItem>}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <TextField
          size="small"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={t('internal-analysts:games_search')}
          inputProps={{ 'aria-label': t('internal-analysts:games_search') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          size="small"
          label={t('internal-analysts:games_filter')}
          value={analyzed}
          onChange={(event) => setParam('analyzed', event.target.value, AnalyzedFilter.Analyzed)}
          sx={{ minWidth: 220 }}
        >
          {analyzedFilters.map((filter) => (
            <MenuItem key={filter.value} value={filter.value}>
              {t(filter.labelKey)}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<Add />} onClick={() => setGameDialog({ open: true })} sx={{ flexShrink: 0 }}>
          {t('internal-analysts:games_add')}
        </Button>
      </Stack>

      {pinnedQuery.isError && (
        <Typography color="error.main" mb={2}>
          {t('internal-analysts:games_pinned_load_failed')}
        </Typography>
      )}

      {renderResults()}

      <EditGameDialog open={gameDialog.open} gameId={gameDialog.gameId} onClose={handleGameDialogClose} onSaved={handleSaved} />
      <GameIdSearchDialog
        open={isIdSearchOpen}
        onClose={() => setIsIdSearchOpen(false)}
        onEdit={(game) => {
          setIsIdSearchOpen(false)
          setGameDialog({ open: true, gameId: game.id })
        }}
        onDelete={(game) => {
          setIsIdSearchOpen(false)
          askDelete(game)
        }}
        onViewAnalyses={(game) => {
          setIsIdSearchOpen(false)
          setAnalysisDialog({ open: true, gameId: game.id })
        }}
      />
      <AnalysisListDialog
        open={analysisDialog.open}
        gameId={analysisDialog.gameId}
        onClose={() => setAnalysisDialog({ open: false })}
        onAnalysisCreated={(id, comparedMarketIso) => analysisDialog.gameId && navigate(getCreatedAnalysisPath(id, analysisDialog.gameId, comparedMarketIso))}
      />
      <VersionTagsDialog
        open={versionTagsDialog.open}
        appId={versionTagsDialog.appId}
        gameName={versionTagsDialog.gameName}
        marketIso={getCellMarketIso(marketIso)}
        onClose={() => setVersionTagsDialog({ open: false, gameName: '' })}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmDelete} />
    </Box>
  )
}
